import { useEffect, useRef } from "react"
import { useNavigate } from "react-router-dom"
import { OverlayPanel } from "primereact/overlaypanel"
import { Button } from "primereact/button"
import { useUnreadCount, useNotifications, useMarkNotificationRead, useMarkAllRead } from "api/useNotifications"
import useAuth0Metadata from "api/useAuth0Metadata"
import { useCustomerData } from "context/customer-provider"
import type { Notification } from "types/thread"

const formatTimeAgo = (dateStr: string) => {
    const seconds = Math.floor((Date.now() - new Date(dateStr).getTime()) / 1000)
    if (seconds < 60) return "just now"
    const minutes = Math.floor(seconds / 60)
    if (minutes < 60) return `${minutes}m ago`
    const hours = Math.floor(minutes / 60)
    if (hours < 24) return `${hours}h ago`
    const days = Math.floor(hours / 24)
    return `${days}d ago`
}

const NotificationItem = ({ notification, onNavigate }: { notification: Notification; onNavigate: (n: Notification) => void }) => (
    <div
        onClick={() => onNavigate(notification)}
        style={{
            padding: "10px 14px",
            cursor: "pointer",
            borderBottom: "1px solid var(--surface-border)",
            backgroundColor: notification.is_read ? "transparent" : "var(--surface-hover)",
        }}
    >
        <div style={{ fontWeight: notification.is_read ? "normal" : 600, fontSize: "0.875rem" }}>{notification.title}</div>
        {notification.body && (
            <div style={{ fontSize: "0.8rem", color: "var(--text-color-secondary)", marginTop: 2, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {notification.body}
            </div>
        )}
        <div style={{ fontSize: "0.75rem", color: "var(--text-color-secondary)", marginTop: 4 }}>{formatTimeAgo(notification.created_at)}</div>
    </div>
)

const NotificationBell = () => {
    const op = useRef<OverlayPanel>(null)
    const { data: unreadCount } = useUnreadCount()
    // Only the count polls. The dropdown's list loads once, follows the count
    // (a new or read notification moves it) and refreshes when the panel opens.
    const { data: notificationsData, refetch: refetchNotifications } = useNotifications({ limit: 10 }, { poll: false })
    const lastCount = useRef(unreadCount)
    useEffect(() => {
        if (unreadCount === undefined) return
        if (lastCount.current !== undefined && lastCount.current !== unreadCount) refetchNotifications()
        lastCount.current = unreadCount
    }, [unreadCount, refetchNotifications])
    const safeUnreadCount = typeof unreadCount === "number" ? unreadCount : 0
    const notifications = Array.isArray(notificationsData) ? notificationsData : []
    const markRead = useMarkNotificationRead()
    const markAllRead = useMarkAllRead()
    const navigate = useNavigate()
    const { userMetadata, updateUserMetadata } = useAuth0Metadata()
    const { product } = useCustomerData()
    // The assignment / @mention email prefs are roads-only features, so hide the
    // whole section for rail customers (NR/RAIL — product "rail"). Rail-specific
    // notification options can be added here later.
    const isRail = product === "rail"
    // Unset → ON for both. The in-app bell always fires; these prefs only gate
    // the matching emails.
    const assignmentEmail = userMetadata?.notificationPrefs?.assignmentEmail !== false
    const mentionEmail = userMetadata?.notificationPrefs?.mentionEmail !== false
    const exportEmail = userMetadata?.notificationPrefs?.exportEmail !== false

    const handleNavigate = (notification: Notification) => {
        if (!notification.is_read) {
            markRead.mutate(notification.id)
        }
        op.current?.hide()
        if (notification.action_url) {
            navigate(notification.action_url)
        }
    }

    return (
        <>
            <li className="mr-2 lg:mr-0">
                <button
                    type="button"
                    className="p-link"
                    onClick={(e) => op.current?.toggle(e)}
                    style={{ position: "relative", overflow: "visible", padding: 2 }}
                >
                    <i className="pi pi-bell" style={{ fontSize: "1.2rem" }} />
                    {safeUnreadCount > 0 && (
                        <span
                            style={{
                                position: "absolute",
                                top: -3,
                                right: -3,
                                minWidth: 16,
                                height: 16,
                                padding: "0 4px",
                                borderRadius: 999,
                                background: "#ef4444",
                                color: "#fff",
                                fontSize: "0.62rem",
                                fontWeight: 700,
                                lineHeight: 1,
                                display: "inline-flex",
                                alignItems: "center",
                                justifyContent: "center",
                                boxSizing: "border-box",
                                zIndex: 1,
                                pointerEvents: "none",
                            }}
                        >
                            {safeUnreadCount > 99 ? "99+" : safeUnreadCount}
                        </span>
                    )}
                </button>
            </li>
            <OverlayPanel ref={op} onShow={() => refetchNotifications()} style={{ width: "360px", maxHeight: "480px", overflow: "auto" }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "8px 14px", borderBottom: "1px solid var(--surface-border)" }}>
                    <span style={{ fontWeight: 600 }}>Notifications</span>
                    <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                        {safeUnreadCount > 0 && (
                            <Button label="Mark all read" className="p-button-text p-button-sm" onClick={() => markAllRead.mutate()} />
                        )}
                        <Button
                            label="View all"
                            icon="pi pi-arrow-right"
                            iconPos="right"
                            className="p-button-text p-button-sm"
                            onClick={() => {
                                op.current?.hide()
                                navigate("/notifications")
                            }}
                        />
                    </div>
                </div>
                {!isRail && (
                <div style={{ padding: "8px 14px", borderBottom: "1px solid var(--surface-border)" }}>
                    <div style={{ fontSize: "0.72rem", color: "var(--text-color-secondary)", marginBottom: 6 }}>
                        In-app alerts always show in your bell. Choose what also reaches your inbox.
                    </div>
                    <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: "0.8rem", cursor: "pointer", color: "var(--text-color-secondary)", padding: "3px 0" }}>
                        <input
                            type="checkbox"
                            checked={assignmentEmail}
                            onChange={(e) => updateUserMetadata({ notificationPrefs: { ...userMetadata?.notificationPrefs, assignmentEmail: e.target.checked } })}
                        />
                        Email me when an assignment is given to me
                    </label>
                    <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: "0.8rem", cursor: "pointer", color: "var(--text-color-secondary)", padding: "3px 0" }}>
                        <input
                            type="checkbox"
                            checked={mentionEmail}
                            onChange={(e) => updateUserMetadata({ notificationPrefs: { ...userMetadata?.notificationPrefs, mentionEmail: e.target.checked } })}
                        />
                        Email me when someone @mentions or replies to me
                    </label>
                    <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: "0.8rem", cursor: "pointer", color: "var(--text-color-secondary)", padding: "3px 0" }}>
                        <input
                            type="checkbox"
                            checked={exportEmail}
                            onChange={(e) => updateUserMetadata({ notificationPrefs: { ...userMetadata?.notificationPrefs, exportEmail: e.target.checked } })}
                        />
                        Email me when my data export is ready
                    </label>
                </div>
                )}
                {notifications.length === 0 ? (
                    <div style={{ padding: "24px", textAlign: "center", color: "var(--text-color-secondary)" }}>No notifications</div>
                ) : (
                    notifications.map((n) => <NotificationItem key={n.id} notification={n} onNavigate={handleNavigate} />)
                )}
            </OverlayPanel>
        </>
    )
}

export default NotificationBell
